<script setup lang="ts">
import type { Manifest } from '@clovyr/pollen/manifest';
import { ImageMedia } from '../media';
import { GridItem, GridSection } from '.';
import Background from '../elements/Background.vue';
import LinodeLogo from '../../assets/images/linode-logo.svg';
import DropletsLogo from '../../assets/images/droplets-logo.png';
import ClovyrHostingFullLogo from '../../assets/images/clovyrhost-lockup.svg';
import AWSLogo from '../../assets/images/aws-logo.svg';
import RedXicon from '../icons/RedXicon.vue';
import GreenCheckIcon from '../icons/GreenCheckIcon.vue';
import AppLogo from '../elements/AppLogo.vue';
import { usePrice } from '../../composables/usePrice';
import NotifyIcon from '../icons/NotifyIcon.vue';

const props = defineProps<{
  app?: Manifest;
  image: string;
  overlayImage?: string;
  isMarketingWebsite?: boolean;
  isComingSoon?: boolean;
}>();

const emit = defineEmits(['launch', 'notify-me']);

const app = computed(() => props.app);

const { wnextAppPrice } = usePrice(app);

const isAppDetail = computed(() => !!props.app);

const CSSClasses = computed(() => {
  return [
    isAppDetail.value && 'app-detail__background',
    !isAppDetail.value && 'collection__background',
  ];
});
const CSSPadding = computed(() => {
  return [!isAppDetail.value && 'extra-padding-1', isAppDetail.value && 'extra-padding-2'];
});
const NotifyMeCSSClasses = computed(() => {
  return [props.isComingSoon && 'coming-soon-disabled'];
});
</script>

<template>
  <div class="pricing">
    <Background
      :image="image"
      :overlay-image="overlayImage"
      class="pricing__background"
      :class="CSSClasses"
    />
    <h2>Pricing</h2>
    <div class="container container-common">
      <div class="row">
        <GridSection :spacing="6">
          <GridItem :md="12" :lg="6">
            <div class="free-trial green"><p>7 day free trial!</p></div>
            <div class="item">
              <h3>Host With Us</h3>
              <div class="host-options" :class="CSSPadding">
                <div class="host-1">
                  <ImageMedia :filename="ClovyrHostingFullLogo" />
                </div>
              </div>
              <div class="host-pricing">
                <div class="wnext-price" v-if="isAppDetail && wnextAppPrice">
                  <h1 class="green">${{ wnextAppPrice }}</h1>
                  <p>/mo</p>
                </div>
                <div class="alt-price" v-else>
                  <p>Starting at</p>
                  <div class="wnext-price">
                    <h1 class="green">$6</h1>
                    <p>/mo</p>
                  </div>
                </div>
              </div>
              <ul class="bullets">
                <li>
                  Flat monthly pricing per app
                  <GreenCheckIcon />
                </li>
                <li>No configuration know-how needed<GreenCheckIcon /></li>
                <li>Access and manage apps privately from any browser<GreenCheckIcon /></li>
                <li>Try apps hassle-free, keep the ones you love!<GreenCheckIcon /></li>
                <li>Apps run on a private VPS<GreenCheckIcon /></li>
                <li>
                  <div class="feature-label">
                    Automated, end-to-end encrypted backup and recovery service included
                  </div>
                  <GreenCheckIcon />
                </li>
                <li>Clovyr technical support<GreenCheckIcon /></li>
              </ul>
            </div>
          </GridItem>
          <GridItem :md="12" :lg="6">
            <div class="item">
              <h3>Bring Your Own Host</h3>
              <div class="host-options" :class="CSSPadding">
                <div class="host">
                  <AppLogo
                    size="small"
                    :logo="{ logoUrl: AWSLogo, logoBackgroundColor: '#ffffff' }"
                  />
                </div>
                <div class="host">
                  <AppLogo
                    size="small"
                    :logo="{ logoUrl: DropletsLogo, logoBackgroundColor: '#ffffff' }"
                  />
                </div>
                <div class="host">
                  <AppLogo
                    size="small"
                    :logo="{ logoUrl: LinodeLogo, logoBackgroundColor: '#ffffff' }"
                  />
                </div>
              </div>
              <div class="host-pricing">
                <div class="wnext-price">
                  <h1 class="green">$0</h1>
                </div>
              </div>
              <ul class="bullets byoh-bullets">
                <li>Free<GreenCheckIcon /></li>
                <li>No configuration know-how needed<GreenCheckIcon /></li>
                <li>Access and manage apps privately from any browser<GreenCheckIcon /></li>
                <li>Try apps hassle-free, keep the ones you love!<GreenCheckIcon /></li>
                <li>Apps run in the cloud account you already have<GreenCheckIcon /></li>
                <li>
                  <div>
                    Automated, end-to-end encrypted backup and recovery service not included
                  </div>
                  <RedXicon />
                </li>
                <li>Clovyr technical support not included<RedXicon /></li>
                <li>No Clovyr account required<GreenCheckIcon /></li>
              </ul>
              <div class="backup">
                <p class="green">Add Clovyr secure backup and recovery service for $5/app</p>
              </div>
            </div>
          </GridItem>
        </GridSection>
        <div class="get-started" v-if="isAppDetail">
          <div
            :class="NotifyMeCSSClasses"
            class="button button--pill button--reversed cta-btn"
            @click="emit('launch')"
          >
            Get Started
          </div>
          <div v-if="isComingSoon" class="coming-soon-link">
            <a
              href="#"
              @click.prevent="
                $emit('notify-me', {
                  appName: props.app?.metadata?.name,
                  appId: props.app?.metadata?.id,
                })
              "
            >
              <NotifyIcon /> Notify me when available
            </a>
          </div>
        </div>
        <div class="get-started" v-if="isMarketingWebsite">
          <a href="https://clovyr.app/" class="button button--pill button--reversed cta-btn">
            Browse the Garden
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'core/colors';
@use 'core/layout/grid-vars';
@use 'vendor/bootstrap/scss/mixins/breakpoints';

.pricing {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: grid-vars.space(7) 0 grid-vars.space(30) 0;
  overflow: hidden;

  .app-detail__background {
    :deep(.background__image) {
      background-size: contain;
      background-position: center top;
    }
  }

  .collection__background {
    :deep(.background__image) {
      background-size: contain;
      background-position: center bottom;
    }

    :deep(.background__overlay-image) {
      background-size: contain;
      background-position: center bottom;
      bottom: -1px;
    }
  }

  h2 {
    padding: grid-vars.space(10) 0;
  }

  .free-trial {
    position: absolute;
    background-color: #383e52;
    border-radius: 8px;
    z-index: 1;
    padding: grid-vars.space(1) grid-vars.space(3);
    top: -3%;
    left: 50%;
    transform: translateX(-50%);
    white-space: pre;
  }

  :deep(.grid-section) {
    [class*='grid__col'] {
      margin-top: grid-vars.space(2);

      @include breakpoints.media-breakpoint-up(md) {
        margin-top: 0;
        padding: 2.5rem;
      }
    }

    [class*='grid__col']:first-child {
      margin-bottom: 2rem;

      @include breakpoints.media-breakpoint-up(lg) {
        padding-right: 1.5rem;
        margin-bottom: 0;
      }
    }
    [class*='grid__col']:last-child {
      @include breakpoints.media-breakpoint-up(lg) {
        padding-left: 1.5rem;
      }
    }

    .grid {
      flex-direction: column;
      align-items: stretch;

      @include breakpoints.media-breakpoint-up(lg) {
        flex-direction: row;
      }

      .grid-item {
        height: 100%;
      }
    }
  }

  :deep([class*='grid__col']) {
    .grid-item {
      background: linear-gradient(180deg, rgb(48 39 68 / 50%) 0%, #302744 100%);
      backdrop-filter: blur(10px);
      border-radius: 18px;
      padding: grid-vars.space(3) grid-vars.space(3) grid-vars.space(3) grid-vars.space(3);
    }
  }

  .item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: grid-vars.space(3) 0 0 0;

    .extra-padding-1 {
      padding-bottom: grid-vars.space(4);
    }

    .extra-padding-2 {
      padding-bottom: grid-vars.space(1);
    }

    .sub-heading {
      font-size: 13px;
    }

    .host-options {
      @extend .item;

      flex-direction: row;
      position: relative;

      .host-1 {
        :deep(.app-icon) {
          .image-media {
            padding: grid-vars.space(1);
          }
        }
      }

      .host {
        display: flex;
        flex-direction: column;
        align-items: center;

        // loop through 5 hosts, increase if more are added

        @for $i from 1 through 5 {
          &:nth-child(#{$i}) {
            z-index: ($i * -1) + 10;
          }
        }

        &:not(:first-child) {
          margin-left: -0.75rem;
        }

        .price {
          padding: 0;
        }

        p {
          font-size: 12px;
          padding: grid-vars.space(1) 0 0 0;
        }

        :deep(.app-icon) {
          .image-media {
            padding: grid-vars.space(1);

            // border: 1px solid #3027441c;
            box-shadow: 0 0 4px 0 #000;
          }
        }
      }
    }

    .host-pricing {
      display: flex;
      flex-direction: column;
      align-items: center;

      .wnext-price {
        display: flex;
        flex-direction: row;
        align-items: baseline;

        h1 {
          padding: 0 grid-vars.space(0.5);
        }
      }

      .alt-price {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
  }

  .bullets {
    padding: grid-vars.space(3) 0 0 0;
    max-width: 95%;

    li {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      color: #e0dde4;
      padding: grid-vars.space(1.5) 0;

      &:not(:last-child) {
        border-bottom: 1px solid #453960;
      }

      div {
        max-width: 80%;
      }

      svg {
        margin-left: 20px;
      }

      :deep(.red-x-icon) {
        height: 20px;
        width: 20px;
      }

      :deep(.green-check-icon) {
        height: 25px;
        width: 25px;
      }
    }
  }

  .byoh-bullets {
    margin-top: 14px;
  }

  .backup {
    background-color: #33e6720d;
    border-radius: 8px;
    padding: grid-vars.space(1.5);
    margin: grid-vars.space(1) 0 0 0;
  }

  .get-started {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .cta-btn {
      margin: grid-vars.space(3) 0 0 0;
      padding: grid-vars.space(1) 10%;
      white-space: pre;
      cursor: pointer;
    }
  }

  .coming-soon-link {
    font-size: 14px;
    margin-top: 0.5rem;

    a {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      color: colors.color(grey, primary);
    }

    .notify-icon {
      margin-right: 0.25rem;
    }
  }

  .coming-soon-disabled {
    opacity: 0.7;
    pointer-events: none;
    cursor: not-allowed;
  }

  .coming-soon-link:hover {
    .notify-icon {
      stroke: colors.color(action, primary);
    }
    a {
      color: colors.color(action, primary);
    }
  }
}

.green {
  color: #33e672;
}

.gray {
  color: #ada6b7;
}
</style>
